import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { FileText, Gavel, Layers, Plus, ScrollText } from "lucide-react";
import { PageHeader } from "@/components/domain/misc";
import { Page } from "@/components/layout/page";
import { RouteTabs } from "@/components/layout/route-tabs";
import { Button } from "@/components/ui/button";
import { isLivePolicy, isOpenClaim } from "@/features/coverage/meta";
import { q } from "@/lib/api";

export const Route = createFileRoute("/_app/coverage")({
  component: CoverageLayout,
});

function TabCount({ n, label }: { n: number; label: string }) {
  if (n <= 0) return null;
  return (
    <span className="ml-0.5 rounded-full bg-surface-3 px-1.5 text-[10.5px] font-semibold leading-4 text-fg-2 tabular" aria-label={label}>
      {n}
    </span>
  );
}

function CoverageLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname.replace(/\/$/, "") });
  // Policy detail and the quote builder are focused pages with their own header and breadcrumb.
  const focused = pathname.startsWith("/coverage/policies/") || pathname === "/coverage/quote";

  const policies = useQuery(q.policies());
  const claims = useQuery(q.claims());
  const org = useQuery(q.org());
  const live = (policies.data ?? []).filter(isLivePolicy);
  const carriers = [...new Set(live.map((p) => p.carrier))];
  const openClaims = claims.data?.filter(isOpenClaim).length ?? 0;

  if (focused) return <Outlet />;

  return (
    <Page>
      <PageHeader
        title="Policies & coverage"
        description={
          policies.data ? (
            <>
              {live.length} {live.length === 1 ? "policy" : "policies"} {org.data ? `${org.data.name} holds` : "held"} over its agents
              {carriers.length ? ` · underwritten by ${carriers.join(" and ")}` : ""}. Each policy covers a chosen set of agents and scenarios with its own limits, terms and claims.
            </>
          ) : policies.isPending ? (
            <span aria-hidden className="inline-block h-4 w-[28rem] animate-pulse rounded-md bg-surface-3/70 align-middle" />
          ) : (
            "The policies you hold over your agents: scenarios, endorsements and claims."
          )
        }
        actions={
          <Button size="sm" asChild>
            <Link to="/coverage/quote">
              <Plus /> New policy
            </Link>
          </Button>
        }
      />
      <RouteTabs
        tabs={[
          { href: "/coverage", label: "Policies", icon: <FileText />, exact: true, badge: <TabCount n={live.length} label={`${live.length} policies`} /> },
          { href: "/coverage/scenarios", label: "Scenarios", icon: <Layers /> },
          { href: "/coverage/endorsements", label: "Endorsements", icon: <ScrollText /> },
          { href: "/coverage/claims", label: "Claims", icon: <Gavel />, badge: <TabCount n={openClaims} label={`${openClaims} open claims`} /> },
        ]}
      />
      <Outlet />
    </Page>
  );
}
